import React, { useMemo } from 'react';
import { Label } from '@artstorefronts/ui';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { MediaDropRegion, profileImageUploader, type UploadItem } from '@arthelper/ui';
import {
  UPLOAD_PROFILE_IMAGE,
  ADMIN_UPLOAD_PROFILE_IMAGE,
  PROCESS_PROFILE_IMAGE,
  ADMIN_PROCESS_PROFILE_IMAGE,
} from '../../graphql/mutations';

interface ImageUploadFieldProps {
  label: string;
  description?: string;
  currentImageUrl?: string | null;
  imageType: 'AVATAR' | 'BANNER';
  onImageUploaded: (url: string | null) => void;
  className?: string;
  isAdminMode?: boolean;
  targetUserId?: string;
}

export const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
  label,
  description,
  currentImageUrl,
  imageType,
  onImageUploaded,
  className = '',
  isAdminMode = false,
  targetUserId,
}) => {
  const { t } = useTranslation('settings');
  const [uploadProfileImage] = useMutation(UPLOAD_PROFILE_IMAGE);
  const [adminUploadProfileImage] = useMutation(ADMIN_UPLOAD_PROFILE_IMAGE);
  const [processProfileImage] = useMutation(PROCESS_PROFILE_IMAGE);
  const [adminProcessProfileImage] = useMutation(ADMIN_PROCESS_PROFILE_IMAGE);

  const uploader = useMemo(
    () =>
      profileImageUploader({
        presign: async (file) => {
          const signed =
            isAdminMode && targetUserId
              ? (
                  await adminUploadProfileImage({
                    variables: { userId: targetUserId, type: imageType, contentType: file.type },
                  })
                ).data?.adminUploadProfileImage
              : (await uploadProfileImage({ variables: { type: imageType, contentType: file.type } })).data
                  ?.uploadProfileImage;
          if (!signed?.success || !signed.url || !signed.key)
            throw new Error(signed?.error || 'Failed to get upload URL');
          return { url: signed.url, key: signed.key };
        },
        process: async (key) => {
          const processed =
            isAdminMode && targetUserId
              ? (await adminProcessProfileImage({ variables: { userId: targetUserId, key, type: imageType } })).data
                  ?.adminProcessProfileImage
              : (await processProfileImage({ variables: { key, type: imageType } })).data?.processProfileImage;
          if (!processed?.success || !processed.url) throw new Error(processed?.error || 'Failed to process image');
          return processed.url as string;
        },
      }),
    [
      adminProcessProfileImage,
      adminUploadProfileImage,
      imageType,
      isAdminMode,
      processProfileImage,
      targetUserId,
      uploadProfileImage,
    ]
  );

  const existing = useMemo(
    () =>
      currentImageUrl
        ? [{ id: `profile:${imageType}:${currentImageUrl}`, url: currentImageUrl, kind: 'image' as const, name: label }]
        : [],
    [currentImageUrl, imageType, label]
  );

  return (
    <div className={className}>
      <Label className="mb-2 block">{label}</Label>
      {description && <p className="text-xs text-gray-500 mb-2">{description}</p>}
      {currentImageUrl && (
        <img
          src={currentImageUrl}
          alt={label}
          className={
            imageType === 'AVATAR'
              ? 'mb-2 h-32 w-32 rounded-lg object-cover'
              : 'mb-2 h-48 w-full rounded-lg object-cover'
          }
        />
      )}
      <MediaDropRegion
        width="100%"
        tone="neutral"
        prompt={t('profile.image-upload.click-to-upload')}
        hint="JPG, PNG · 5 MB"
        accept={['image/jpeg', 'image/jpg', 'image/png']}
        maxSizeMB={5}
        multiple={false}
        existing={existing}
        uploader={uploader}
        onItemComplete={(item: UploadItem) => onImageUploaded(item.result as string)}
        onItemError={(_, error) =>
          toast.error(t('profile.image-upload.upload-error', { label: label.toLowerCase(), error: error.message }))
        }
        onRemove={(item: UploadItem) => {
          if (item.phase === 'done') onImageUploaded(null);
        }}
      />
    </div>
  );
};
